// src/features/boe/components/programs/Program0400.jsx
import React from "react";
import { ProgramBudget } from "../../../../budget/model/program/ProgramBudget.jsx";

/**
 * Configuration object defining how Program 0400 (English / Reading)
 * maps Firestore budget data into editable, year-based budget tables.
 *
 * The <ProgramBudget /> engine uses this config to produce:
 * - Approved & Actual budget tables
 * - UI field groupings (positions, salaries, services, materials)
 * - Value extraction rules from nested Firestore blocks
 *
 * @typedef {Object} ProgramFieldDefinition
 * @property {string} key - Unique internal identifier used for rendering & state management.
 * @property {string} label - Human-readable description for the UI.
 * @property {"currency"|"count"|"text"} valueType - Determines formatting (currency, FTE count, etc.).
 * @property {Array<{block: string, key: string}>} sources - One or more Firestore budget data sources.
 * @property {"positions"|"salaries"|"services"|"materials"} group - Logical section grouping in the UI.
 * @property {boolean} defaultVisible - Whether the field is visible by default in tables.
 *
 * @typedef {Object} ProgramTableDefinition
 * @property {string} id - Unique table ID ("approvedMain", "actualMain").
 * @property {string} title - Table title shown in the UI header.
 * @property {string} sectionKey - Firestore block key ("Approved" or "Actual").
 * @property {string[]} fields - Ordered list of field keys to include in the table.
 * @property {Object<string, Object>} columnStyles - Optional CSS width constraints keyed by field.
 *
 * @typedef {Object} ProgramConfig
 * @property {string} fullName - Program display name.
 * @property {string} docId - Firestore document identifier for this program.
 * @property {ProgramFieldDefinition[]} fieldDefs - All field definitions belonging to this program.
 * @property {ProgramTableDefinition[]} tables - Approved/Actual table definitions.
 * @property {?string[]} inflationSumFields - Optional fields used for inflation aggregation.
 */

/**
 * @type {ProgramConfig}
 */
export const PROGRAM_0400_CONFIG = {
  fullName: "ENGLISH / READING",
  docId: "0400",

  /**
   * Field definitions describing how to access, label, and display
   * English / Reading budget data from nested Firestore structures.
   */
  fieldDefs: [
    // -----------------------------------------------------------
    // POSITIONS (FTE)
    // -----------------------------------------------------------
    {
      key: "positionsAdmin",
      label: "Positions – Administrative",
      valueType: "count",
      sources: [{ block: "Positions", key: "Administrative" }],
      group: "positions",
      defaultVisible: false,
    },
    {
      key: "positionsTeachersEnglish",
      label: "Positions – Teachers (English)",
      valueType: "count",
      sources: [{ block: "Positions", key: "Teachers (English)" }],
      group: "positions",
      defaultVisible: false,
    },
    {
      key: "positionsTeachersReading",
      label: "Positions – Teachers (Reading)",
      valueType: "count",
      sources: [{ block: "Positions", key: "Teachers (Reading)" }],
      group: "positions",
      defaultVisible: false,
    },

    // -----------------------------------------------------------
    // SALARIES
    // -----------------------------------------------------------
    {
      key: "salariesAdmin",
      label: "Salaries – Administrative",
      valueType: "currency",
      sources: [{ block: "Salaries", key: "Administrative" }],
      group: "salaries",
      defaultVisible: false,
    },
    {
      key: "salariesTeachersEnglish",
      label: "Salaries – Teachers (English)",
      valueType: "currency",
      sources: [{ block: "Salaries", key: "Teachers (English)" }],
      group: "salaries",
      defaultVisible: false,
    },
    {
      key: "salariesTeachersReading",
      label: "Salaries – Teachers (Reading)",
      valueType: "currency",
      sources: [{ block: "Salaries", key: "Teachers (Reading)" }],
      group: "salaries",
      defaultVisible: false,
    },
    {
      key: "tutors",
      label: "Tutors",
      valueType: "currency",
      sources: [{ block: "Salaries", key: "Tutors" }],
      group: "salaries",
      defaultVisible: false,
    },
    {
      key: "fieldTripsEnglish",
      label: "Field Trips (English)",
      valueType: "currency",
      sources: [{ block: "Salaries", key: "Field Trips (English)" }],
      group: "salaries",
      defaultVisible: false,
    },

    // -----------------------------------------------------------
    // SUPPLIES / MATERIALS
    // -----------------------------------------------------------
    {
      key: "instructionalMaterialsEnglish",
      label: "Instructional Materials (English)",
      valueType: "currency",
      sources: [
        {
          block: "Supplies / Materials",
          key: "Instructional Materials (English)",
        },
      ],
      group: "materials",
      defaultVisible: false,
    },
    {
      key: "instructionalMaterialsReading",
      label: "Instructional Materials (Reading)",
      valueType: "currency",
      sources: [
        {
          block: "Supplies / Materials",
          key: "Instructional Materials (Reading)",
        },
      ],
      group: "materials",
      defaultVisible: false,
    },
    {
      key: "testingMaterialsReading",
      label: "Testing Materials (Reading)",
      valueType: "currency",
      sources: [
        {
          block: "Supplies / Materials",
          key: "Testing Materials (Reading)",
        },
      ],
      group: "materials",
      defaultVisible: false,
    },
    {
      key: "technologySoftwareEnglish",
      label: "Technology Software (English)",
      valueType: "currency",
      sources: [
        {
          block: "Supplies / Materials",
          key: "Technology Software (English)",
        },
      ],
      group: "materials",
      defaultVisible: false,
    },

    // -----------------------------------------------------------
    // OTHER
    // -----------------------------------------------------------
    {
      key: "duesFees",
      label: "Dues & Fees",
      valueType: "currency",
      sources: [{ block: "Other", key: "Dues and Fees" }],
      group: "other",
      defaultVisible: false,
    },
  ],

  /**
   * Table configurations defining how the Approved and Actual
   * Program 0400 views are rendered in the UI.
   */
  tables: [
    {
      id: "approvedMain",
      title: "0400 ENGLISH / READING – Approved",
      sectionKey: "Approved",
      fields: [
        "positionsAdmin",
        "positionsTeachersEnglish",
        "positionsTeachersReading",
        "salariesAdmin",
        "salariesTeachersEnglish",
        "salariesTeachersReading",
        "tutors",
        "fieldTripsEnglish",
        "instructionalMaterialsEnglish",
        "instructionalMaterialsReading",
        "testingMaterialsReading",
        "technologySoftwareEnglish",
        "duesFees",
      ],
      columnStyles: {
        year: { width: "100px", minWidth: "100px" },

        // Positions
        positionsAdmin: { minWidth: "130px" },
        positionsTeachersEnglish: { minWidth: "150px" },
        positionsTeachersReading: { minWidth: "150px" },

        // Salaries
        salariesAdmin: { minWidth: "130px" },
        salariesTeachersEnglish: { minWidth: "150px" },
        salariesTeachersReading: { minWidth: "150px" },
        tutors: { minWidth: "90px" },
        fieldTripsEnglish: { minWidth: "100px" },

        // Materials / Other
        instructionalMaterialsEnglish: { minWidth: "160px" },
        instructionalMaterialsReading: { minWidth: "160px" },
        testingMaterialsReading: { minWidth: "160px" },
        technologySoftwareEnglish: { minWidth: "160px" },
        duesFees: { minWidth: "80px" },
      },
    },
    {
      id: "actualMain",
      title: "0400 ENGLISH / READING – Actual",
      sectionKey: "Actual",
      fields: [
        "positionsAdmin",
        "positionsTeachersEnglish",
        "positionsTeachersReading",
        "salariesAdmin",
        "salariesTeachersEnglish",
        "salariesTeachersReading",
        "tutors",
        "fieldTripsEnglish",
        "instructionalMaterialsEnglish",
        "instructionalMaterialsReading",
        "testingMaterialsReading",
        "technologySoftwareEnglish",
        "duesFees",
      ],
      columnStyles: {
        year: { width: "100px", minWidth: "100px" },

        // Positions
        positionsAdmin: { minWidth: "130px" },
        positionsTeachersEnglish: { minWidth: "150px" },
        positionsTeachersReading: { minWidth: "150px" },

        // Salaries
        salariesAdmin: { minWidth: "130px" },
        salariesTeachersEnglish: { minWidth: "150px" },
        salariesTeachersReading: { minWidth: "150px" },
        tutors: { minWidth: "90px" },
        fieldTripsEnglish: { minWidth: "100px" },

        // Materials / Other
        instructionalMaterialsEnglish: { minWidth: "160px" },
        instructionalMaterialsReading: { minWidth: "160px" },
        testingMaterialsReading: { minWidth: "160px" },
        technologySoftwareEnglish: { minWidth: "160px" },
        duesFees: { minWidth: "80px" },
      },
    },
  ],

  // No inflation aggregation for this program
  inflationSumFields: null,
};

/**
 * Program 0400 – ENGLISH / READING
 *
 * Renders the English / Reading budget configuration into a <ProgramBudget /> component.
 * Provides:
 * - Automatic generation of Approved/Actual tables
 * - Editing support (via props from BoardOfEducation)
 * - Mapped Firestore field extraction based on PROGRAM_0400_CONFIG
 *
 * @component
 * @param {Object} props - Props forwarded to <ProgramBudget />.
 * @param {boolean} props.isEditing - Enables editing mode for the tables.
 * @param {Function} props.setBoeData - Callback used to collect edited values at the BoE level.
 * @returns {JSX.Element}
 */
export function Program0400(props) {
  return (
    <ProgramBudget programId="0400" config={PROGRAM_0400_CONFIG} {...props} />
  );
}
